import React from 'react';
import { Link } from 'react-router-dom';
import { ENV_CONFIG } from '../../constants';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  GlobeAltIcon 
} from '@heroicons/react/24/outline';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    membership: [
      { name: 'Categories', href: '/membership/categories' },
      { name: 'Benefits', href: '/membership/benefits' },
      { name: 'Code of Ethics', href: '/membership/code-of-ethics' },
      { name: 'Application', href: '/membership/application' },
    ],
    resources: [
      { name: 'Publications', href: '/publications' },
      { name: 'Training & Events', href: '/training-events' },
      { name: 'Sponsorship', href: '/sponsorship' },
      { name: 'Technical Reports', href: '/technical-reports' },
    ],
    organization: [
      { name: 'About Us', href: '/about' },
      { name: 'Contact', href: '/contact' },
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
    ],
  };

  const socialLinks = [
    { name: 'Twitter', href: 'https://twitter.com/csean_ng', icon: 'twitter' },
    { name: 'LinkedIn', href: 'https://linkedin.com/company/csean', icon: 'linkedin' },
    { name: 'YouTube', href: 'https://youtube.com/@csean', icon: 'youtube' },
    { name: 'Instagram', href: 'https://instagram.com/csean_ng', icon: 'instagram' },
  ];

  const getSocialIcon = (platform: string) => {
    const iconProps = { className: "w-5 h-5" };
    
    switch (platform) {
      case 'twitter':
        return (
          <svg {...iconProps} fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
          </svg>
        );
      case 'linkedin':
        return (
          <svg {...iconProps} fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        );
      case 'youtube':
        return (
          <svg {...iconProps} fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        );
      case 'instagram':
        return (
          <svg {...iconProps} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.281h-1.523v1.523h1.523v-1.523zm-3.262 3.262c-.807 0-1.523-.716-1.523-1.523s.716-1.523 1.523-1.523 1.523.716 1.523 1.523-.716 1.523-1.523 1.523z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-max">
        <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="w-20 h-16 flex items-center justify-center bg-gray-100 rounded-lg p-3">
                  <img 
                    src="/cseans-logo.png" 
                    alt="CSEAN Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">{ENV_CONFIG.siteName}</h3>
                <p className="text-sm text-gray-400">
                  {ENV_CONFIG.siteTagline}
                </p>
              </div>
            </div>
            
            <p className="text-gray-400 mb-6 leading-relaxed">
              Building a secure digital future for Nigeria through professional excellence, 
              knowledge sharing, and collaborative cybersecurity initiatives.
            </p>

            {/* Contact Information */}
            <div className="space-y-3">
              <div className="flex items-center text-gray-400">
                <EnvelopeIcon className="w-5 h-5 mr-3" />
                <a href="mailto:info@csean.org.ng" className="hover:text-white transition-colors">
                  info@csean.org.ng
                </a>
              </div>
              <div className="flex items-center text-gray-400">
                <PhoneIcon className="w-5 h-5 mr-3" />
                <a href="tel:+2341234567890" className="hover:text-white transition-colors">
                  +234 (703) 930 6310
                </a>
              </div>
              <div className="flex items-center text-gray-400">
                <MapPinIcon className="w-5 h-5 mr-3" />
                <span>Abuja, Nigeria</span>
              </div>
            </div>
          </div>

          {/* Membership Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Membership</h4>
            <ul className="space-y-2">
              {footerLinks.membership.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Resources</h4>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Organization Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Organization</h4>
            <ul className="space-y-2">
              {footerLinks.organization.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social Media */}
            <div className="mt-6">
              <h5 className="text-sm font-semibold mb-3">Follow Us</h5>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label={social.name}
                  >
                    {getSocialIcon(social.icon)}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {currentYear} {ENV_CONFIG.siteName}. All Rights Reserved.
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <span>•</span>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms of Service
              </Link>
              <span>•</span>
              <a 
                href="https://csean.org.ng" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white transition-colors flex items-center"
              >
                <GlobeAltIcon className="w-4 h-4 mr-1" />
                Official Site
              </a>
            </div>
          </div>
          
          {/* Developer Credit */}
          <div className="mt-6 pt-6 border-t border-gray-800 text-center">
            <a
              href="https://wa.me/2348110302009?text=Hi%20Dan%2C%20I%20found%20you%20through%20the%20CSEAN%20website.%20I%20would%20like%20to%20talk%20about%20a%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-white transition-colors text-sm"
            >
              Built by DAN
            </a>
          </div>
        </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
